import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useLayoutEffect,
} from 'react'
import {
  isTimeoutError,
  parseResponseBody,
  prepareBody,
  requestSocketWithTimeout,
} from './query'
import hash from 'stable-hash'
import Store from '../redux/store'


/**
 * For GET request, use useEffect to fetch data automatically if needed
 * For mutation, use send() to send data
 * For just send data and receive response without any state management, use requestSocket* functions in `query.ts`
 */
function useSocketRequest({
  appServiceType,
  timeout = 30000,
}) {
  const [state, setState] = useState({
    status: 'idle',
  })

  const requestMapRef = useRef(new Map())

  // Cleanup function for a request
  const cleanupRequest = useCallback((requestId) => {
    const request = requestMapRef.current.get(requestId)
    if (request) {
      clearTimeout(request.timeoutId)
      request.unsubscribe()
      requestMapRef.current.delete(requestId)
    }
  }, [])

  // Cleanup all pending requests
  const cleanupAllRequests = useCallback(() => {
    requestMapRef.current.forEach((_, requestId) => {
      cleanupRequest(requestId)
    })
  }, [cleanupRequest])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAllRequests()
    }
  }, [cleanupAllRequests])

  const send = useCallback(
    (body, { eraseData = false } = {}) => {
      // Cleanup any existing requests before making a new one
      cleanupAllRequests()

      setState((prev) => {
        if (prev.status !== 'loading') {
          return { status: 'loading', data: eraseData ? undefined : prev.data }
        }

        if (eraseData && prev.data !== undefined) {
          return { status: 'loading', data: undefined }
        }

        return prev
      })

      return new Promise((resolve, reject) => {
        const { unsubscribe, requestId, timeoutId } = requestSocketWithTimeout(
          {
            appServiceType,
            body: prepareBody({
              ...body,
              namespace: Store.getState().namespace,
            }),
          },
          {
            timeout,
            onError: (errorResponse) => {
              if (isTimeoutError(errorResponse)) {
                const error = {
                  message: `Request timed out after ${timeout}ms`,
                }
                cleanupRequest(requestId)
                setState({ status: 'error', error })
                reject(error)

                return
              }

              const error = {
                message: errorResponse.message || 'Unknown error',
                code: errorResponse.result,
              }

              setState({ status: 'error', error })
              reject(error)
            },
            onSuccess: (response) => {
              cleanupRequest(requestId)
              try {
                const parsedData = parseResponseBody(response)

                setState({ status: 'success', data: parsedData })
                resolve(parsedData)
              } catch (e) {
                const error = {
                  message: 'Failed to parse response data',
                  originalError: e,
                }

                setState({ status: 'error', error })
                reject(error)
              }
            },
          }
        )

        // Store all cleanup-related items
        requestMapRef.current.set(requestId, {
          resolve,
          reject,
          unsubscribe,
          timeoutId,
        })
      })
    },
    [appServiceType, timeout, cleanupRequest, cleanupAllRequests]
  )

  const reset = useCallback(() => {
    cleanupAllRequests()
    setState({ status: 'idle', data: undefined })
  }, [cleanupAllRequests])

  const patch = useCallback(
    (patcher) => {
      setState((prev) => {
        console.log('patcher', prev.data, patcher(prev.data))
        return { ...prev, data: patcher(prev.data) }
      })
    },
    []
  )

  return {
    data: state.data,
    error: state.status === 'error' ? state.error : null,
    isLoading: state.status === 'loading',
    status: state.status,
    send,
    reset,
    patch,
  }
}

/**
 * React-query style of fetching data
 * Should be used for GET request only
 */
export const useSocketQuery = (body, options) => {
  const result = useSocketRequest(options)

  useLayoutEffect(() => {
    console.log('useLayoutEffect', body)
    result.send(body, { eraseData: false })
  }, [hash(body)])

  return result
}

export { useSocketRequest }
